import React, { useState, useEffect } from "react";
import { Card, CardContent, Typography, TextField, Button, Box, Divider, Chip, Grid2, Autocomplete, Link } from "@mui/material";
import { useScheduleService } from "../services/ScheduleService";
import moment from "moment";

const ViewEditLesson = ({
  lessons,
  lesson,
  students,
  teachers,
  mode = "view",
  onSubmit,
  onSave,
  onReschedule,
  onClose,
  hideStudents = false,
}) => {
  const [form, setForm] = useState(() => lesson ? {
      ...lesson,
      startDttm: new Date(lesson.startDttm),
      rStartDttm: lesson.rStartDttm ? new Date(lesson.rStartDttm) : null,
      rEndDttm: lesson.rEndDttm ? new Date(lesson.rEndDttm) : null,
    } : {}
  );
  const [readonly, setReadonly] = useState(mode === "view");
  const [errors, setErrors] = useState({});

  const { openSchedule } = useScheduleService();

  const validateReschedule = () => {
    let errs = {};
  
    if (!form.rStartDttm) {
      errs.rStartDttm = "Rescheduled start is required";
    }
  
    if (!form.rEndDttm) {
      errs.rEndDttm = "Rescheduled end is required";
    }
  
    if (form.rStartDttm && form.rEndDttm) {
      if (new Date(form.rEndDttm) <= new Date(form.rStartDttm)) {
        errs.rEndDttm = "Rescheduled end must be after start";
      }
    }
  
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
    

  useEffect(() => {
    setForm( lesson ? {
        ...lesson,
        rStartDttm: lesson.rStartDttm ? new Date(lesson.rStartDttm) : null,
        rEndDttm: lesson.rEndDttm ? new Date(lesson.rEndDttm) : null,
      } : {}
    );
    setReadonly(mode === "view");
  }, [lesson, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    onSubmit(form);
    // if (mode === "create") {
    //   setForm({});
    // } else {
    //   setReadonly(true);
    // }
  };

  const handleSave = () => {
    const lessonChanges = {};
    Object.keys(form).forEach((key) => {
      if (form[key] !== lesson[key]) {
        lessonChanges[key] = form[key];
      }
    });
    if (Object.keys(lessonChanges).length === 0) return;
    onSave(lesson.uid, lessonChanges);
  }

  const handleReschedule = () => {
    if (!validateReschedule()) {
      return;
    }
    const lessonChanges = {};
    Object.keys(form).forEach((key) => {
      if (form[key] !== lesson[key]) {
        lessonChanges[key] = form[key];
      }
    });
    onReschedule(lesson.uid, lessonChanges);
  }

  const handleRangeSelected = (range) => {
    setForm((prev) => ({ ...prev, startDttm: range?.start, endDttm: range?.end }));
    // form.startDttm = range?.start.toLocaleString();
    // form.endDttm = range?.end.toLocaleString();
  };

  const isSafeUrl = (url) => {
    try {
      const parsed = new URL(url);
      return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  }

  if (!lesson && mode !== "create") {
    return (
      <Box
        sx={{
          border: "2px dashed #b0b0b0",
          borderRadius: 2,
          height: "100%",
          minHeight: "400px",
          width: "620px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "gray",
          bgcolor: "#fafafa",
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Select Lesson
        </Typography>
      </Box>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: "2px dashed #b0b0b0",
        boxShadow: 0,
        width: "520px",
        bgcolor: "#ffffff",
      }}
    >
      <CardContent>
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", mb: 2, color: "#0d47a1", textAlign: "center" }}
        >
          {mode === "create" ? "Create Lesson" : "Lesson Details"}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid2 container spacing={2}>
          <Grid2>
            <Box mb={2} height="55px">
              <TextField
                label="Lesson Name"
                name="name"
                fullWidth
                value={form.name || ""}
                onChange={handleChange}
                slotProps={{ input: { readOnly: mode !== "edit" && mode !== "create" } }}
              />
            </Box>

            <Box mb={2}>
              <TextField
                label="Lesson Comment"
                name="comment"
                fullWidth
                multiline
                value={form.comment || ""}
                onChange={handleChange}
                slotProps={{ input: { readOnly: mode !== "edit" && mode !== "create" } }}
              />
            </Box>

            <Box mb={2} display="flex" alignItems="center" gap={1} height="55px">
              <TextField
                label="Start Date & Time"
                name="startDttm"
                type="text"
                fullWidth
                value={form.startDttm ? moment(form.startDttm).format("MM/DD/YYYY - HH:mm") : ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setForm((prev) => ({ ...prev, startDttm: moment(val, "YYYY-MM-DD HH:mm").toDate() || prev.startDttm }));
                }}
                onClick={() => {
                  if (mode === "reschedule" || mode === "create")
                    openSchedule(lesson, lessons, () => {}, handleRangeSelected);
                }}
                slotProps={{
                  input: {
                    readOnly: mode !== "create"
                  },
                }}
              />
            </Box>

            <Box mb={2} display="flex" alignItems="center" gap={1} height="55px">
              <TextField
                label="End Date & Time"
                name="endDttm"
                type="text"
                fullWidth
                value={form.endDttm ? moment(form.endDttm).format("MM/DD/YYYY - HH:mm") : ""}
                onChange={handleChange}
                onClick={() => {
                  if (mode === "reschedule" || mode === "create")
                    openSchedule(lesson, lessons, () => {}, handleRangeSelected);
                }}
                slotProps={{
                  input: {
                    readOnly: mode !== "create"
                  },
                }}
              />
            </Box>

            {hideStudents ? (<></>) : (
              <Box mb={2}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  autoHighlight
                  clearIcon={null}
                  options={students || teachers}
                  getOptionLabel={(option) => option.firstName + " " + option.lastName}
                  value={form.students || []}
                  readOnly={mode !== "create"}
                  onChange={(event, newValue) => {
                    if (!readonly) {
                      setForm((prev) => ({
                        ...prev,
                        students: newValue,
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Students" fullWidth />
                  )}
                  renderTags={(value, getTagProps) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        width: "100%",
                      }}
                    >
                      {value.map((student, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={student.uid}
                          label={student.firstName + " " + student.lastName}
                        />
                      ))}
                    </Box>
                  )}
                />
              </Box>)
          }

          <Box mb={2}>
            <TextField
              label="Fee"
              name="fee"
              type="number"
              fullWidth
              value={form.fee || 0}
              onChange={handleChange}
              slotProps={{ input: { readOnly: mode !== "edit" && mode !== "create" } }}
            />
          </Box>

          <Box mb={2}>
            <TextField
              label="Currency"
              name="ccy"
              fullWidth
              value={form.ccy || ""}
              onChange={(e) => setForm((p) => ({ ...p, ccy: e.target.value.toUpperCase() }))}
              slotProps={{ input: { readOnly: mode !== "edit" && mode !== "create", maxLength: 4 } }}
            />
          </Box>

          <Box mb={2}>
            { mode !== "edit" && mode !== "create" ? (
              isSafeUrl(form.link) ? (
                <Link href={form.link} target="_blank" rel="noopener noreferrer">
                  Join Lesson
                </Link>
              ) : (
                <span>Link is not safe</span>
              )
            ) : (
              <TextField
                label="Lesson Link"
                name="link"
                fullWidth
                value={form.link || ""}
                onChange={handleChange}
              />
            )}
          </Box>
        </Grid2>
        <Grid2>
          <Box mb={2} height="55px">
            <Typography
              variant="h6"
              sx={{ fontWeight: "2px", mb: 2, color: "#0d47a1", textAlign: "left" }}
            >
              STATUS: {form.status}
            </Typography>
          </Box>

          {(mode === "reschedule" || form.status === 'rescheduled' || form.status === 'moved') && (
              <>
                <Box mb={2} display="flex" alignItems="center" gap={1} height="55px">
                  <TextField
                    label="Rescheduled Start"
                    name="rStartDttm"
                    type="text"
                    fullWidth
                    disabled={mode !== "reschedule"}
                    value={
                      form.rStartDttm
                        ? moment(form.rStartDttm).format("MM/DD/YYYY - HH:mm")
                        : ""
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        rStartDttm:
                          moment(val, "MM/DD/YYYY - HH:mm").toDate() ||
                          prev.rStartDttm,
                      }));
                      setErrors(prev => ({ ...prev, rStartDttm: null }));
                    }}
                    onClick={() =>
                      openSchedule(lesson, lessons, () => {}, (range) => {
                        setForm((prev) => ({
                          ...prev,
                          rStartDttm: range?.start,
                          rEndDttm: range?.end,
                        }));
                      })
                    }
                    slotProps={{ input: { readOnly: false } }}
                    error={!!errors.rStartDttm}
                    helperText={errors.rStartDttm}
                  />
                </Box>

                <Box mb={2} display="flex" alignItems="center" gap={1} height="55px">
                  <TextField
                    label="Rescheduled End"
                    name="rEndDttm"
                    type="text"
                    fullWidth
                    disabled={mode !== "reschedule"}
                    value={
                      form.rEndDttm
                        ? moment(form.rEndDttm).format("MM/DD/YYYY - HH:mm")
                        : ""
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        rEndDttm:
                          moment(val, "MM/DD/YYYY - HH:mm").toDate() ||
                          prev.rEndDttm,
                      }));
                      setErrors(prev => ({ ...prev, rEndDttm: null }));
                    }}
                    onClick={() =>
                      openSchedule(lesson, lessons, () => {}, (range) => {
                        setForm((prev) => ({
                          ...prev,
                          rStartDttm: range?.start,
                          rEndDttm: range?.end,
                        }));
                        setErrors({});
                      })
                    }
                    slotProps={{ input: { readOnly: false } }}
                    error={!!errors.rEndDttm}
                    helperText={errors.rEndDttm}
                  />
                </Box>
              </>
            )}
          
        </Grid2>
        {/* Status Actions */}
      </Grid2>

      <Box display="flex" gap={2} justifyContent="flex-end" mt={3}>
        <Button onClick={onClose}>Close</Button>
        {
          mode === "edit" ? (
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          ) : mode === "create" ? (
            <Button variant="contained" onClick={handleSubmit} color="primary">
              Submit
            </Button>
          ) : mode === "reschedule" ? (
            <Button variant="contained" onClick={handleReschedule} color="primary">
              Save
            </Button>
          ) : <></>
        }
      </Box>
    </CardContent>
    </Card >
  );
};

export default ViewEditLesson;
